/**
 * Which pages the secure payment frame may load, and which messages from them we believe.
 *
 * Two kinds of form: LivePerson's own (the existing path, matched by pattern) and, since CPOC-143,
 * our own card form for Connect chats, matched by EXACT origin from per-environment config
 * (VITE_SECURE_FORM_ORIGINS, empty in prod). A vendor's address replaces ours by config alone.
 * Nothing here ever sees card data: a form page tells us only a state word.
 */

/** LivePerson's secure form page (unchanged from TRACI-1012). */
const LP_FORM_URL = /^https:\/\/[a-z0-9.-]+\.liveperson\.net\/pcigw\//;
/** The origin a LivePerson form page may post from. Wider than the one URL on purpose: a
 *  redirect inside LivePerson's domain must not break the prod form. */
const ORIGIN_SHAPE = /^https:\/\/[a-z0-9]([a-z0-9.-]*[a-z0-9])?(:[0-9]{1,5})?$/;

/** The state words our own form page may post (as `{state}`, nothing else). */
export const OWN_FORM_STATES = ['ready', 'submitted', 'received', 'not-confirmed', 'unreachable', 'expired', 'error'] as const;
export type OwnFormState = (typeof OWN_FORM_STATES)[number];

export type FormKind = 'liveperson' | 'own';

/** The configured card form origins: exact `https://host[:port]`, lower case. A malformed entry is dropped. */
export function configuredFormOrigins(raw?: string): string[] {
  const value = raw !== undefined ? raw : (import.meta.env.VITE_SECURE_FORM_ORIGINS as string | undefined) || '';
  const out: string[] = [];
  for (const part of value.split(',')) {
    const o = part.trim().toLowerCase();
    if (o && ORIGIN_SHAPE.test(o) && !out.includes(o)) out.push(o);
  }
  return out;
}

/** What kind of form this address is, or null when it may not be framed at all. */
export function formKind(url: unknown, origins: string[] = configuredFormOrigins()): FormKind | null {
  if (typeof url !== 'string') return null;
  if (LP_FORM_URL.test(url)) return 'liveperson';
  let u: URL;
  try { u = new URL(url); } catch { return null; }
  if (u.protocol !== 'https:' || u.username || u.password) return null;
  return origins.includes(u.origin) ? 'own' : null;
}

/** May the message from `origin` be believed for a form at `url`? */
export function messageOriginOk(origin: string, url: string): boolean {
  try { return origin === new URL(url).origin; } catch { return false; }
}

/** Our own form's message, or null. Strict: a plain object whose only key is `state`, one of the
 *  listed words. No other field is read or kept. */
export function readOwnFormState(data: unknown): OwnFormState | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const keys = Object.keys(data as object);
  if (keys.length !== 1 || keys[0] !== 'state') return null;
  const state = (data as { state?: unknown }).state;
  return typeof state === 'string' && (OWN_FORM_STATES as readonly string[]).includes(state) ? (state as OwnFormState) : null;
}
